import React from 'react';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { Divider } from '@mui/material';

import { allLockTargets, LockTarget } from '@paragon/data-connections';
import { getChildrenFinder } from '@paragon/layout-models';
import {
  hasCanUsePlatesPermission,
  hasHangerUpgradeAndAnalyzeFeatureFlag,
  hasTrussConversionPermission,
  isLoggedInUserEngineer,
} from '@paragon/login-tools';

import { useAppMonitor } from '../../../../monitor';
import { batchLock, batchLockEverything, batchUnlockEverything } from '../../../../store/actions/lockOptionsActions';
import { showConfirmAffectAllLocksDialog } from '../../../../store/actions/popupActions';
import {
  getComponentDesignDescendantModels,
  getComponentDesignDescendantsLockingStatus,
} from '../../../../store/selectors/batchLockingSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import CascadingSceneMenuItem from '../common/CascadingSceneMenuItem';
import { SceneMenuItem } from '../common/SceneMenuItem';

type SupportedType = 'project' | 'building' | 'level' | 'componentDesign';

export function LockOptionsSubMenu(props: { type: SupportedType; guid: string }) {
  const dispatch = useAppDispatch();
  const monitor = useAppMonitor();

  const isEngineer = useAppSelector(isLoggedInUserEngineer);
  const hasPermissionToConvertTrusses = useAppSelector(hasTrussConversionPermission);
  const canUsePlates = useAppSelector(hasCanUsePlatesPermission);
  const allowHangerLocking = useAppSelector(hasHangerUpgradeAndAnalyzeFeatureFlag);

  const allComponentDesigns = useAppSelector((state) => state.componentDesigns.data);
  const childrenFinder = useAppSelector(getChildrenFinder);

  const componentDesigns =
    props.type === 'componentDesign'
      ? [allComponentDesigns[props.guid]]
      : getComponentDesignDescendantModels(childrenFinder, allComponentDesigns, props.guid);

  if (componentDesigns.length === 0) return null;

  const {
    allDescendantsHaveAnalogLocked,
    allDescendantsHaveHangersLocked,
    allDescendantsHaveLoadingLocked,
    allDescendantsHaveMembersLocked,
    allDescendantsHavePlatingLocked,
    someDescendantsHaveLoadingLocked,
    someDescendantsHaveSomethingLocked,
    someLockOptionIsIndeterminate,
    anythingIsLocked,
  } = getComponentDesignDescendantsLockingStatus(componentDesigns);

  const componentDesignDescendantGuids = componentDesigns.map((componentDesign) => componentDesign.guid);

  // Lock Everything must respect permissions, just like the existing category lock & unlock buttons (users cannot lock
  //   categories using the Lock Everything buttons that they cannot also lock & unlock using the category buttons).
  const canLockLoading = isEngineer || hasPermissionToConvertTrusses;
  const userCanLock: Record<LockTarget, boolean> = {
    analog: canLockLoading,
    loading: canLockLoading,
    plating: canUsePlates,
    members: true, // everyone can lock members
    hangers: allowHangerLocking,
  };
  const lockTargetsUserCanLock = allLockTargets.filter((lockTarget) => userCanLock[lockTarget]);

  const allDescendantsLocked: Record<LockTarget, boolean> = {
    analog: allDescendantsHaveAnalogLocked,
    loading: allDescendantsHaveLoadingLocked,
    plating: allDescendantsHavePlatingLocked,
    members: allDescendantsHaveMembersLocked,
    hangers: allDescendantsHaveHangersLocked,
  };
  const anythingLockableIsUnlocked = lockTargetsUserCanLock.some((lockTarget) => !allDescendantsLocked[lockTarget]);

  return (
    <CascadingSceneMenuItem icon={anythingIsLocked ? <LockIcon /> : <LockOpenIcon />} label="Locking Options">
      {isEngineer && (
        <SceneMenuItem
          icon={allDescendantsHaveAnalogLocked ? <LockIcon /> : <LockOpenIcon />}
          text={allDescendantsHaveAnalogLocked ? 'Unlock Analog Models' : 'Lock Analog Models'}
          disabled={!isEngineer || someDescendantsHaveLoadingLocked}
          onClick={() => {
            dispatch(batchLock(componentDesignDescendantGuids, 'analog', !allDescendantsHaveAnalogLocked));
            monitor.recordInteraction('Loading lock toggled for batch of components');
          }}
        />
      )}

      <SceneMenuItem
        icon={allDescendantsHaveLoadingLocked ? <LockIcon /> : <LockOpenIcon />}
        text={allDescendantsHaveLoadingLocked ? 'Unlock Loading' : 'Lock Loading'}
        disabled={!isEngineer && !hasPermissionToConvertTrusses}
        onClick={() => {
          dispatch(batchLock(componentDesignDescendantGuids, 'loading', !allDescendantsHaveLoadingLocked));
          monitor.recordInteraction('Loading lock toggled for batch of components');
        }}
      />

      {canUsePlates ? (
        <SceneMenuItem
          icon={allDescendantsHavePlatingLocked ? <LockIcon /> : <LockOpenIcon />}
          text={allDescendantsHavePlatingLocked ? 'Unlock Plating' : 'Lock Plating'}
          onClick={() => {
            dispatch(batchLock(componentDesignDescendantGuids, 'plating', !allDescendantsHavePlatingLocked));
            monitor.recordInteraction('Plating lock toggled for batch of components');
          }}
        />
      ) : null}

      <SceneMenuItem
        icon={allDescendantsHaveMembersLocked ? <LockIcon /> : <LockOpenIcon />}
        text={allDescendantsHaveMembersLocked ? 'Unlock Lumber' : 'Lock Lumber'}
        onClick={() => {
          dispatch(batchLock(componentDesignDescendantGuids, 'members', !allDescendantsHaveMembersLocked));
          monitor.recordInteraction('Member lock toggled for batch of components');
        }}
      />

      {allowHangerLocking ? (
        <SceneMenuItem
          icon={allDescendantsHaveHangersLocked ? <LockIcon /> : <LockOpenIcon />}
          text={allDescendantsHaveHangersLocked ? 'Unlock Hangers' : 'Lock Hangers'}
          onClick={() => {
            dispatch(batchLock(componentDesignDescendantGuids, 'hangers', !allDescendantsHaveHangersLocked));
            monitor.recordInteraction('Hanger lock toggled for batch of components');
          }}
        />
      ) : null}

      {someDescendantsHaveSomethingLocked || anythingLockableIsUnlocked ? <Divider /> : null}

      {anythingLockableIsUnlocked ? (
        <SceneMenuItem
          icon={<LockIcon />}
          text="Lock Everything"
          onClick={() => {
            // Locking a uniformly unlocked scope is easy to redo by hand, but a scope where only part of a lock
            //   option is unlocked isn't, so confirm before applying that.
            if (someLockOptionIsIndeterminate) {
              dispatch(
                showConfirmAffectAllLocksDialog({
                  componentDesignGuids: componentDesignDescendantGuids,
                  lockTargets: lockTargetsUserCanLock,
                  action: 'lock',
                }),
              );
            } else {
              void dispatch(batchLockEverything(componentDesignDescendantGuids, lockTargetsUserCanLock));
            }
          }}
        />
      ) : null}

      {someDescendantsHaveSomethingLocked ? (
        <SceneMenuItem
          icon={<LockOpenIcon />}
          text="Unlock Everything"
          onClick={() => {
            // Unlocking a uniformly locked scope is easy to redo by hand, but a scope where only part of a lock
            //   option is locked isn't, so confirm before discarding that.
            if (someLockOptionIsIndeterminate) {
              dispatch(
                showConfirmAffectAllLocksDialog({
                  componentDesignGuids: componentDesignDescendantGuids,
                  lockTargets: allLockTargets,
                  action: 'unlock',
                }),
              );
            } else {
              void dispatch(batchUnlockEverything(componentDesignDescendantGuids, allLockTargets));
            }
          }}
        />
      ) : null}
    </CascadingSceneMenuItem>
  );
}
